import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AccessibilityInfo, FlatList, Platform, ScrollView, StyleSheet, View } from 'react-native';

import { StoreLocatorEmptyStates } from 'components/store-locator-empty-states';
import { IRestaurantNode } from 'generated/rbi-graphql';
import { useGeolocation } from 'state/geolocation';
import { OrderLocatorTab } from 'utils/launchdarkly/variations';
import { STORE_LOCATOR_STORE_LIST_ITEM_AT } from 'utils/test-ids';

import LoadingIndicator from '../../loading-indicator';
import {
  focusStoreLocatorFirstRestaurantResult,
  hasPendingStoreLocatorFirstRestaurantFocus,
  setStoreLocatorFirstRestaurantFocusTarget,
  subscribeStoreLocatorFirstRestaurantFocusRequest,
} from '../../store-locator-focus-handoff';
import { StoreActions } from '../store-actions';
import { StoreCard } from '../store-card';
import type { StoreCardProps } from '../store-card/types';

import { StoreCardWrapper } from './store-card-list.styled';

interface IStoreCardListProps {
  currentTabHasError?: boolean;
  isSearching?: boolean;
  restaurants: IRestaurantNode[];
  tab: OrderLocatorTab;
  onCardPress?: (storeId?: string, storeNumber?: string, storeAddress?: string) => void;
}

const StoreCardItem = ({
  isSelected,
  restaurant,
  onCardClick,
  testID,
  isFirst = false,
  isStoreLocatorFocusTarget,
  storeLocatorFocusRef,
}: StoreCardProps) => {
  return (
    <StoreCardWrapper>
      <StoreCard
        restaurant={restaurant}
        onCardClick={onCardClick}
        isSelected={isSelected}
        testID={testID}
        isFirst={isFirst}
        isStoreLocatorFocusTarget={isStoreLocatorFocusTarget}
        storeLocatorFocusRef={storeLocatorFocusRef}
      />
      {isSelected ? <StoreActions restaurant={restaurant} /> : null}
    </StoreCardWrapper>
  );
};

const StoreCardList: React.FC<IStoreCardListProps> = ({
  currentTabHasError = false,
  isSearching,
  restaurants = [],
  tab,
  onCardPress,
}) => {
  const flatListRef = useRef<FlatList>(null);
  const storeLocatorFocusTargetRef = useRef<HTMLButtonElement | null>(null);
  const { formatMessage } = useIntl();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectionAnnouncement, setSelectionAnnouncement] = useState('');
  const [focusRequestVersion, setFocusRequestVersion] = useState(0);
  const { coordinatesAvailable, isPermissionGranted } = useGeolocation();

  const setStoreLocatorFocusTargetRef = useCallback((node: HTMLButtonElement | null) => {
    storeLocatorFocusTargetRef.current = node;
    setStoreLocatorFirstRestaurantFocusTarget(node);
  }, []);

  const loadingAriaLabel = useMemo(
    () =>
      tab === OrderLocatorTab.NEARBY
        ? formatMessage({ id: 'loadingNearbyLocations' })
        : 'Loading locations. Please wait.',
    [formatMessage, tab]
  );

  const announceSelection = useCallback((message: string) => {
    if (!message) {
      return;
    }

    if (Platform.OS !== 'web') {
      AccessibilityInfo.announceForAccessibility(message);
      return;
    }

    setSelectionAnnouncement('');
    requestAnimationFrame(() => {
      setSelectionAnnouncement(message);
    });
  }, []);

  const selectedStoreIndex = useMemo(() => {
    const firstAvailableStoreIndex = restaurants.findIndex(
      restaurant => restaurant.isAvailable !== false
    );
    return firstAvailableStoreIndex >= 0 ? firstAvailableStoreIndex : 0;
  }, [restaurants]);

  useEffect(() => {
    return subscribeStoreLocatorFirstRestaurantFocusRequest(() => {
      setFocusRequestVersion(version => version + 1);
    });
  }, []);

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      isSearching ||
      !restaurants.length ||
      !hasPendingStoreLocatorFirstRestaurantFocus()
    ) {
      return;
    }

    let frameId: number | null = null;
    let attempts = 0;

    const focusFirstStoreResult = () => {
      attempts += 1;
      if (focusStoreLocatorFirstRestaurantResult()) {
        return;
      }

      if (attempts < 8) {
        frameId = window.requestAnimationFrame(focusFirstStoreResult);
      }
    };

    const timeoutId = window.setTimeout(focusFirstStoreResult, 0);

    return () => {
      window.clearTimeout(timeoutId);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [focusRequestVersion, isSearching, restaurants.length, selectedStoreIndex]);

  const handleCardClick = useCallback(
    (index: number) => {
      if (selectedIndex === index) {
        setSelectedIndex(null);
        announceSelection('Store selection cleared.');
        return;
      }

      setSelectedIndex(index);
      announceSelection(buildStoreSelectedAnnouncement(restaurants[index]));

      setTimeout(() => {
        flatListRef.current?.scrollToItem({
          item: restaurants[index],
          viewPosition: 1,
        });
      }, 300);
    },
    [announceSelection, restaurants, selectedIndex]
  );

  if (isSearching) {
    return <LoadingIndicator ariaLabel={loadingAriaLabel} />;
  }

  if (restaurants.length === 0) {
    return (
      <ScrollView>
        <StoreLocatorEmptyStates
          tab={tab}
          locationPermissionsGranted={isPermissionGranted}
          coordinatesAvailable={coordinatesAvailable}
          currentTabError={currentTabHasError}
        />
      </ScrollView>
    );
  }

  return (
    <>
      {Platform.OS === 'web' ? (
        <View
          style={styles.visuallyHidden}
          {...({
            'aria-live': 'polite',
            'aria-atomic': true,
            role: 'status',
          } as Record<string, unknown>)}
        >
          {selectionAnnouncement}
        </View>
      ) : null}
      <FlatList
        ref={flatListRef}
        keyExtractor={(item, index) => item?.id || index + '_restaurant'}
        data={restaurants}
        removeClippedSubviews={false}
        getItemLayout={undefined}
        {...(Platform.OS === 'web' ? ({ role: 'presentation' } as Record<string, unknown>) : {})}
        renderItem={({ item, index }) => {
          const handleCardPress = onCardPress
            ? () =>
                onCardPress(
                  String(item.id),
                  String(item.number),
                  String(item.physicalAddress?.address1)
                )
            : () => handleCardClick(index);

          return (
            <StoreCardItem
              testID={STORE_LOCATOR_STORE_LIST_ITEM_AT(index)}
              restaurant={item}
              onCardClick={handleCardPress}
              isSelected={index === selectedIndex}
              isFirst={index === 0}
              isStoreLocatorFocusTarget={index === selectedStoreIndex}
              storeLocatorFocusRef={
                index === selectedStoreIndex ? setStoreLocatorFocusTargetRef : undefined
              }
            />
          );
        }}
      />
    </>
  );
};

function buildStoreSelectedAnnouncement(restaurant: IRestaurantNode) {
  const restaurantName = restaurant.name?.trim() || 'Store';
  return `Selected store: ${restaurantName}.`;
}

const styles = StyleSheet.create({
  visuallyHidden: {
    borderWidth: 0,
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: 1,
  },
});

export default React.memo(StoreCardList, (prevProps, nextProps) => {
  if (prevProps.tab !== nextProps.tab) {
    return false;
  }
  if (prevProps.isSearching !== nextProps.isSearching) {
    return false;
  }
  if (prevProps.currentTabHasError !== nextProps.currentTabHasError) {
    return false;
  }
  if (!isEqual(prevProps.restaurants, nextProps.restaurants)) {
    return false;
  }

  return true;
});
